/**
 * 物件用の構造化データを生成する
 * @param {Object} room - 物件データ
 * @returns {Object} JSON-LDオブジェクト
 */
export const getRoomSchema = (room) => {
    if (!room) return null;

    return {
        "@context": "https://schema.org",
        "@type": "Apartment",
        "name": `${room.location.prefecture} ${room.location.city}の${room.propertyType || 'ルームシェア物件'}`,
        "description": room.description || `${room.location.prefecture} ${room.location.city}のルームシェア物件`,
        "address": {
            "@type": "PostalAddress",
            "addressLocality": room.location.city,
            "addressRegion": room.location.prefecture,
            "addressCountry": "JP"
        },
        "numberOfRooms": 1,
        "floorSize": {
            "@type": "QuantitativeValue",
            "value": room.roomDetails?.size || 0,
            "unitCode": "MTK"  // 平方メートル
        },
        "amenityFeature": Array.isArray(room.roomDetails?.amenities)
            ? room.roomDetails.amenities.map(amenity => ({
                "@type": "LocationFeatureSpecification",
                "name": amenity
            }))
            : [],
        "geo": room.location?.coordinates ? {
            "@type": "GeoCoordinates",
            "latitude": room.location.coordinates[1] || 0,
            "longitude": room.location.coordinates[0] || 0
        } : undefined,
        "offers": {
            "@type": "Offer",
            "price": room.rentamount,
            "priceCurrency": "JPY",
            "availability": "https://schema.org/InStock"
        },
        "image": Array.isArray(room.images) ? room.images : []
    };
};

/**
 * 検索結果用の構造化データを生成する
 * @param {Array} rooms - 物件データの配列
 * @returns {Object} JSON-LDオブジェクト
 */
export const getSearchResultSchema = (rooms) => {
    if (!Array.isArray(rooms) || rooms.length === 0) return null;

    return {
        "@context": "https://schema.org",
        "@type": "SearchResultsPage",
        "mainEntity": {
            "@type": "ItemList",
            "itemListElement": rooms.map((room, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "item": {
                    "@type": "Apartment",
                    "name": `${room.location.prefecture} ${room.location.city}の物件`,
                    "description": room.description || `${room.location.prefecture} ${room.location.city}のルームシェア物件`,
                    "address": {
                        "@type": "PostalAddress",
                        "addressLocality": room.location.city,
                        "addressRegion": room.location.prefecture
                    },
                    "offers": {
                        "@type": "Offer",
                        "price": room.rentamount,
                        "priceCurrency": "JPY"
                    }
                }
            }))
        }
    };
};

/**
 * ルームメイト検索結果用の構造化データを生成する
 * @param {Array} users - ユーザーデータの配列
 * @returns {Object} JSON-LDオブジェクト
 */
export const getRenterSearchResultSchema = (users) => {
    if (!Array.isArray(users) || users.length === 0) return null;

    return {
        "@context": "https://schema.org",
        "@type": "SearchResultsPage",
        "mainEntity": {
            "@type": "ItemList",
            "itemListElement": users.map((user, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "item": {
                    "@type": "Person",
                    "name": user.profile?.nickname || '名前非公開',
                    "gender": user.profile?.gender || '',
                    "description": user.profile?.bio || 'ルームメイト募集中',
                    "image": user.profile?.profileImage || ''
                }
            }))
        }
    };
};

/**
 * ユーザープロフィール用の構造化データを生成する
 * @param {Object} user - ユーザーデータ
 * @returns {Object} JSON-LDオブジェクト
 */
export const getUserSchema = (user) => {
    if (!user || !user.profile) return null;

    return {
        "@context": "https://schema.org",
        "@type": "Person",
        "name": user.profile.nickname || '',
        "description": user.profile.bio || '',
        "gender": user.profile.gender || '',
        "image": user.profile.profileImage || ''
    };
};

/**
 * サイト全体の組織情報の構造化データを生成する
 * @returns {Object} JSON-LDオブジェクト
 */
export const getOrganizationSchema = () => {
    return {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Room Share Matching",
        "url": "https://roomshare-matching.com",
        "logo": "https://roomshare-matching.com/images/logo.png",
        "sameAs": [

        ],
        "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+81-03-XXXX-XXXX",
            "contactType": "customer service",
            "availableLanguage": ["Japanese", "English"]
        }
    };
};

/**
 * パンくずリスト用の構造化データを生成する
 * @param {Array} items - パンくずリストの項目配列
 * @returns {Object} JSON-LDオブジェクト
 */
export const getBreadcrumbSchema = (items) => {
    if (!Array.isArray(items) || items.length === 0) return null;

    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": items.map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.name,
            "item": `https://roomshare-matching.com${item.path}`
        }))
    };
}; 